<template>
  <div class="row">
    <div class="col-12">
      <div class="row mb-3">
        <div class="col-12">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <h1>App Types</h1>
          </div>
          <p>You can manage the app types here.<br /></p>
        </div>
      </div>
      <div class="row mb-3">
        <div class="col-12">
          <StoreTable
            :initial-per-page="20"
            :refresh-function="reload"
            :items="appTypes"
            :fields="fields"
            :filter-fn="filterFn"
            :initial-sort-by="[{ key: 'id', order: 'asc' }]"
          >
            <template #cell(actions)="data">
              <StoreButton @click="openUpdateAppTypeModal(data.item)"
                >Update</StoreButton
              >
            </template>
          </StoreTable>
        </div>
      </div>
    </div>
    <UpdateAppTypeModal
      v-if="appTypeToUpdate"
      v-model="showUpdateAppTypeModal"
      :app-type="appTypeToUpdate"
      :callback-function="updateAppType"
    />
  </div>
</template>

<script lang="ts" setup>
import userIsLoggedIn from "~/middleware/userIsLoggedIn";
import userIsAdmin from "~/middleware/userIsAdmin";
import type { TableField } from "bootstrap-vue-next";
import type { WebModelGetAppType } from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import { useToastStore } from "~/stores/ui/toaststore";
import StoreTable from "../../../components/shared/tables/StoreTable.vue";
import StoreButton from "~/components/shared/buttons/StoreButton.vue";
import type { AppTypeForm } from "~/validations/adminarea/app-type";
import UpdateAppTypeModal from "~/components/specific/admin/apptypes/UpdateAppTypeModal.vue";
import { useAppTypesStore } from "~/stores/apptypesstore";

definePageMeta({
  layout: "admin",
  middleware: [userIsLoggedIn, userIsAdmin],
});

const appTypesStore = useAppTypesStore();

const showUpdateAppTypeModal = ref(false);
const appTypeToUpdate = ref<WebModelGetAppType | null>(null);

const appTypes = computed(() => appTypesStore.appTypes);

const reload = async () => {
  await appTypesStore.loadAppTypes();
};

const fields: TableField[] = [
  { key: "id", label: "Id", sortable: true },
  { key: "name", label: "Name", sortable: true },
  { key: "hasPlcInstallation", label: "Has PLC Installation", sortable: true },
  { key: "hasDownload", label: "Has Download", sortable: true },
  { key: "plcLicensable", label: "PLC Licensable", sortable: true },
  { key: "engineerLibrary", label: "Engineer Library", sortable: true },
  { key: "engineerPlugin", label: "Engineer Plugin", sortable: true },
  {
    key: "productcodesFromStoreRange",
    label: "Productcodes From Store Range",
    sortable: true,
  },
  { key: "analyseJsonFile", label: "Analyse Json File", sortable: true },
  { key: "acceptedFileFormat", label: "File Formats", sortable: true },
  { key: "helpText", label: "Help Text", sortable: true },
  { key: "actions", label: "", sortable: false },
];

const filterFn = (item: unknown, search: string): boolean => {
  const m = item as WebModelGetAppType | null;
  const s = (search ?? "").trim().toLowerCase();
  if (!s) return true;

  return (
    (m?.name ?? "").toLowerCase().includes(s) ||
    (m?.hasPlcInstallation ?? "").toString().toLowerCase().includes(s) ||
    (m?.hasDownload ?? "").toString().toLowerCase().includes(s) ||
    (m?.plcLicensable ?? "").toString().toLowerCase().includes(s) ||
    (m?.engineerLibrary ?? "").toString().toLowerCase().includes(s) ||
    (m?.engineerPlugin ?? "").toString().toLowerCase().includes(s) ||
    (m?.productcodesFromStoreRange ?? "")
      .toString()
      .toLowerCase()
      .includes(s) ||
    (m?.analyseJsonFile ?? "").toString().toLowerCase().includes(s) ||
    (m?.id ?? "").toString().toLowerCase().includes(s) ||
    (m?.helpText ?? "").toLowerCase().includes(s) ||
    (m?.acceptedFileFormat ?? "").toString().toLowerCase().includes(s)
  );
};

const toastStore = useToastStore();
const updateAppType = async (form: AppTypeForm) => {
  if (!appTypeToUpdate.value || !appTypeToUpdate.value.id) {
    toastStore.addToastWithMessageAndTitle(
      "Error",
      "Missing Parameters, cannot update.",
      "danger",
    );
    return;
  }

  await appTypesStore.updateAppTypeById(appTypeToUpdate.value.id, {
    helpText: form.helpText,
  });
  showUpdateAppTypeModal.value = false;
  appTypeToUpdate.value = null;
  toastStore.addToastWithMessageAndTitle(
    "Updated",
    "App type updated successfully",
    "success",
  );
};

const openUpdateAppTypeModal = async (item: WebModelGetAppType) => {
  appTypeToUpdate.value = item;
  showUpdateAppTypeModal.value = true;
};

useSeoMeta({ title: "PLCnext Store | Admin | App Types" });
</script>
